import {isPassportIdentity} from '@sollapay/domain';
import {PartyType} from '@sollapay/enums';
import {useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {FullNameField} from '@/components';
import {
  IdentityDocumentFields,
  identityFieldsCellClassName,
  identityNameCellClassName
} from '@/components/identity';
import {AiFlashWrapper} from '@/features/ai-extraction';
import {BuyerKycDocumentUpload} from '@/features/buyers/components';
import {
  generateHebrewPersonName,
  generateIdentityNumber,
  withDebugSetValue
} from '@/features/debug-test-data';
import {useCurrentTrustAccount} from '@/features/trust-accounts/providers';
import {fullNameRule, idCountryRule, individualIdRule} from '@/lib/validation';

import type {useAiFillFlash, useFieldAiProps} from '@/features/ai-extraction';
import type {BuyerEntryFormValues, CreateBuyerFormValues} from '@/features/buyers/validation';
import type {DebugSetValue} from '@/features/debug-test-data';
import type {FC, ReactNode} from 'react';
import type {UseFormReturn} from 'react-hook-form';

export interface BuyerIdentityFieldsProps {
  form: UseFormReturn<CreateBuyerFormValues>;
  index: number;
  fieldAi: ReturnType<typeof useFieldAiProps>;
  isFlashing: ReturnType<typeof useAiFillFlash>;
  uploadStatus?: ReactNode;
}

export const BuyerIdentityFields: FC<BuyerIdentityFieldsProps> = ({
  form,
  index,
  fieldAi,
  isFlashing,
  uploadStatus
}) => {
  const {t} = useTranslation('trustAccounts');
  const {control, register, formState} = form;
  const {id: trustAccountId} = useCurrentTrustAccount();
  const setDebugValue = form.setValue as DebugSetValue;
  const fp = <K extends keyof BuyerEntryFormValues['person'] & string>(field: K) =>
    `buyers.${index}.person.${field}` as `buyers.0.person.${K}`;

  const idType = useWatch({control, name: fp('idType')});
  const buyerKey = form.getValues(fp('buyerKey'));
  const isPassport = isPassportIdentity(idType);
  const idNumberAi = fieldAi(fp('nationalId'));
  const idCountryAi = fieldAi(fp('idCountry'));
  const errors = formState.errors.buyers?.[index]?.person;

  return (
    <>
      <BuyerKycDocumentUpload
        partyType={PartyType.PERSON}
        idType={idType}
        trustAccountId={trustAccountId}
        metadata={{buyerKey}}
        onDocumentsLoaded={([documentId = '']) =>
          form.setValue(fp('identityDocumentId'), documentId, {shouldDirty: true})
        }
      />
      {uploadStatus}

      <div className="grid grid-cols-2 gap-6">
        <AiFlashWrapper
          field={fp('fullName')}
          fieldAi={fieldAi}
          isFlashing={isFlashing}
          className={identityNameCellClassName(isPassport)}
        >
          {ai => (
            <FullNameField
              label={t('view.buyers.addBuyerModal.buyerDetails.name')}
              error={errors?.fullName?.message}
              required
              cornerBadge={withDebugSetValue(
                ai.aiIndicator,
                setDebugValue,
                fp('fullName'),
                generateHebrewPersonName,
                ai.onAiChange
              )}
              isLoading={ai.aiGenerating}
              {...register(fp('fullName'), {...fullNameRule(t), onChange: () => ai.onAiChange()})}
            />
          )}
        </AiFlashWrapper>
        <AiFlashWrapper
          flashing={isFlashing(fp('nationalId')) || isFlashing(fp('idCountry'))}
          className={identityFieldsCellClassName(isPassport)}
        >
          <IdentityDocumentFields
            control={control}
            isPassport={isPassport}
            idNumberName={fp('nationalId')}
            idCountryName={fp('idCountry')}
            idNumberRules={individualIdRule(t, isPassport)}
            idCountryRules={idCountryRule(t)}
            idNumberError={errors?.nationalId?.message}
            idCountryError={errors?.idCountry?.message}
            cornerBadge={withDebugSetValue(
              idNumberAi.aiIndicator,
              setDebugValue,
              fp('nationalId'),
              () => generateIdentityNumber(idType),
              idNumberAi.onAiChange
            )}
            isLoading={idNumberAi.aiGenerating}
            onIdNumberChange={idNumberAi.onAiChange}
            idCountryCornerBadge={idCountryAi.aiIndicator}
            idCountryIsLoading={idCountryAi.aiGenerating}
            onIdCountryChange={idCountryAi.onAiChange}
          />
        </AiFlashWrapper>
      </div>
    </>
  );
};
